import { Injectable, Injector, OnDestroy, inject, runInInjectionContext, signal } from '@angular/core';
import {
  Auth,
  FacebookAuthProvider,
  GoogleAuthProvider,
  TwitterAuthProvider,
  User,
  authState,
  confirmPasswordReset,
  linkWithPopup,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  unlink,
  updatePassword,
  user,
  verifyPasswordResetCode,
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import { AppUpdateService } from '@core/services/app-update.service';
import { TranslateService } from '@ngx-translate/core';
import { MaintenanceService } from '@pages/maintenance/maintenance.service';
import * as Sentry from '@sentry/angular';
import { SignalTriggerUtilitiesService } from '@utilities/signal-trigger-utilities.service';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  catchError,
  debounceTime,
  firstValueFrom,
  from,
  of,
  retry,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { IUser } from '../interfaces/user.interface';
import { UserGroupService } from './user-group.service';
import { UserService } from './user.service';

// Constants
const TOKEN_EXPIRY_MS = 55 * 60 * 1000; // Token expires in 1 hour, refresh 5 minutes before expiry
const DEBOUNCE_TIME_MS = 300; // Debounce token refresh requests by 300ms
const AUTH_STATE_RETRIES = 3; // Number of retries for auth state

interface ITokenCache {
  token: string;
  expiry: number;
}

interface IFirebaseUser extends User {
  reloadUserInfo: {
    createdAt: string;
    disabled: boolean;
    email: string;
    emailVerified: boolean;
    lastLoginAt: string;
    lastRefreshAt: string;
    localId: string;
    passwordHash: string;
    passwordUpdatedAt: number;
    providerUserInfo: {
      email: string;
      federatedId: string;
      providerId: string;
      rawId: string;
    };
    validSince: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private static readonly currentDbUserSubject = new BehaviorSubject<IUser | null>(null);
  private static readonly currentFirebaseUserSubject = new BehaviorSubject<IFirebaseUser | null>(null);
  private readonly isInitialized = new BehaviorSubject<boolean>(false);
  private readonly isAuthenticating = new BehaviorSubject<boolean>(true);

  public readonly currentFirebaseUser$ = AuthService.currentFirebaseUserSubject.asObservable();
  public readonly currentDbUser$ = AuthService.currentDbUserSubject.asObservable();
  public readonly isAuthenticating$ = this.isAuthenticating.asObservable();

  private readonly tokenRefreshedSubject = new Subject<void>();
  public readonly tokenRefreshed$ = this.tokenRefreshedSubject.asObservable();

  public readonly userGroupsChanged = signal<boolean>(false);

  private readonly auth: Auth = inject(Auth);
  private readonly router: Router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly userService = inject(UserService);
  private readonly translate = inject(TranslateService);
  private readonly appUpdateService = inject(AppUpdateService);
  private readonly maintenanceService = inject(MaintenanceService);
  private readonly userGroupService = inject(UserGroupService);

  private readonly subscriptions = new Subscription();
  private readonly tokenCache = new Map<string, ITokenCache>();
  private tokenRefreshInProgress = false;
  private isSigningIn = false;
  private returnUrl: string | null = null;

  constructor() {
    this.initAuthState();
  }

  public static get currentFirebaseUser(): IFirebaseUser | null {
    return AuthService.currentFirebaseUserSubject.value;
  }

  public static get currentDbUser(): IUser | null {
    return AuthService.currentDbUserSubject.value;
  }

  public static set currentDbUserProfile(profile: {
    name: string | null;
    language: string | null;
    iconId: string | null;
  }) {
    const currentUser = AuthService.currentDbUser;

    if (currentUser) {
      AuthService.currentDbUserSubject.next({
        ...currentUser,
        iconId: profile.iconId,
        language: profile.language,
        name: profile.name,
      });
    }
  }

  public async signInWithGoogle(): Promise<void> {
    await this.signInWithProvider(new GoogleAuthProvider(), 'Google sign-in');
  }

  public async signInWithFacebook(): Promise<void> {
    await this.signInWithProvider(new FacebookAuthProvider(), 'Facebook sign-in');
  }

  public async signInWithTwitter(): Promise<void> {
    await this.signInWithProvider(new TwitterAuthProvider(), 'Twitter sign-in');
  }

  public async signInWithEmail(email: string, password: string): Promise<void> {
    try {
      this.prepareForSignIn();
      await this.runInAuthContext(async (auth) => {
        await signInWithEmailAndPassword(auth, email, password);
      });
    } catch (error) {
      this.handleSignInError(error, 'Email sign-in');
    }
  }

  public async signInWithCustomToken(token: string): Promise<void> {
    try {
      this.prepareForSignIn();
      await this.runInAuthContext(async (auth) => {
        await signInWithCustomToken(auth, token);
      });
    } catch (error) {
      this.handleSignInError(error, 'Custom token sign-in');
    }
  }

  public async signOut(redirect = true): Promise<void> {
    try {
      await this.runInAuthContext(async (auth) => {
        await signOut(auth);
      });
      this.clearTokenCache();
      AuthService.currentFirebaseUserSubject.next(null);
      AuthService.currentDbUserSubject.next(null);
      Sentry.setUser(null);
      // Awaited (unlike the other call sites of clearImageDataGroupCache) - see
      // AppUpdateService.clearImageDataGroupCache for why this exists (the service worker's image
      // cache is keyed by URL alone, with no notion of which user is signed in). At least one
      // caller (accept-creator-invitation.component.ts's handleSignIn) chains signOut() straight
      // into signInWithCustomToken() for a different user with no delay in between; if this were
      // fire-and-forget, that next sign-in's image requests could race the cache clear and get
      // served the previous user's cached images. Awaiting here closes that race for every current
      // and future caller that chains a sign-in (or anything else) immediately after signOut(),
      // for the small, fixed cost of a few Cache Storage calls that signOut() - already async -
      // was never on a hard latency budget for anyway.
      await this.appUpdateService.clearImageDataGroupCache();

      if (redirect) {
        await this.router.navigate(['/auth/login']);
      }
    } catch (error) {
      this.handleAuthError(error, 'Sign out');
    }
  }

  public reinitAuthState(): void {
    this.initAuthState();
  }

  public getIdToken(forceRefresh = false): Observable<string | null> {
    return new Observable<string | null>((subscriber) => {
      const subscription = this.runInAuthContext((auth) =>
        user(auth)
          .pipe(
            debounceTime(DEBOUNCE_TIME_MS),
            switchMap((authUser) => this.handleTokenRequest(authUser, forceRefresh)),
            shareReplay({ bufferSize: 1, refCount: true }),
          )
          .subscribe(subscriber),
      );

      this.subscriptions.add(subscription);

      return () => subscription.unsubscribe();
    });
  }

  public refreshToken(): Observable<string | null> {
    if (this.tokenRefreshInProgress) {
      return this.getIdToken();
    }

    this.tokenRefreshInProgress = true;

    return new Observable<string | null>((subscriber) => {
      const subscription = this.runInAuthContext((auth) =>
        from(auth.currentUser?.getIdToken(true) || Promise.resolve(null))
          .pipe(
            debounceTime(DEBOUNCE_TIME_MS),
            map((token) => this.handleRefreshedToken(token, auth.currentUser)),
            catchError((error) => this.handleTokenError(error)),
            shareReplay({ bufferSize: 1, refCount: true }),
          )
          .subscribe(subscriber),
      );

      this.subscriptions.add(subscription);

      return () => subscription.unsubscribe();
    });
  }

  public isAuthenticated(): Observable<boolean> {
    return this.waitForInitialization().pipe(
      switchMap(() =>
        this.currentFirebaseUser$.pipe(
          switchMap((currentUser) =>
            !currentUser
              ? this.refreshToken().pipe(map((token) => !!token))
              : this.currentDbUser$.pipe(map((dbUser) => !!dbUser)),
          ),
        ),
      ),
      catchError((error) => {
        this.handleAuthError(error, 'Authentication check');
      }),
    );
  }

  public waitForInitialization(): Observable<boolean> {
    return this.isInitialized.pipe(
      filter((initialized) => initialized),
      map(() => true),
    );
  }

  public async forgotPassword(email: string): Promise<void> {
    try {
      await firstValueFrom(this.userService.sendPasswordResetEmail(email));
    } catch (error) {
      this.handleAuthError(error, 'Forgot password');
    }
  }

  public async forgotPasswordFirebase(email: string): Promise<void> {
    try {
      await this.runInAuthContext(async (auth) => {
        await sendPasswordResetEmail(auth, email);
      });
    } catch (error) {
      this.handleAuthError(error, 'Password reset');
    }
  }

  public async verifyPasswordResetCode(oobCode: string): Promise<string> {
    try {
      return await this.runInAuthContext(async (auth) => {
        const email = await verifyPasswordResetCode(auth, oobCode);

        return email;
      });
    } catch (error) {
      this.handleAuthError(error, 'Verify password reset code');
    }
  }

  public async confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
    try {
      await this.runInAuthContext(async (auth) => {
        await confirmPasswordReset(auth, oobCode, newPassword);
      });
    } catch (error) {
      this.handleAuthError(error, 'Confirm password reset');
    }
  }

  public async updatePassword(password: string): Promise<void> {
    try {
      await this.runInAuthContext(async (auth) => {
        await updatePassword(auth.currentUser!, password);
      });
    } catch (error) {
      this.handleAuthError(error, 'Password reset');
    }
  }

  public async reloadCurrentUser(): Promise<void> {
    try {
      await this.runInAuthContext(async (auth) => {
        await auth.currentUser?.reload();
        AuthService.currentFirebaseUserSubject.next((auth.currentUser as IFirebaseUser | null) ?? null);
      });
    } catch (error) {
      this.handleAuthError(error, 'User reload');
    }
  }

  public async linkWithGoogle(): Promise<void> {
    try {
      await this.runInAuthContext(async (auth) => {
        if (!auth.currentUser) {
          return;
        }

        const provider = new GoogleAuthProvider();
        await linkWithPopup(auth.currentUser, provider);
      });
    } catch (error) {
      this.handleAuthError(error, 'Google account linking');
    }
  }

  public async linkWithFacebook(): Promise<void> {
    try {
      await this.runInAuthContext(async (auth) => {
        if (!auth.currentUser) {
          return;
        }

        const provider = new FacebookAuthProvider();
        await linkWithPopup(auth.currentUser, provider);
      });
    } catch (error) {
      this.handleAuthError(error, 'Facebook account linking');
    }
  }

  public async linkWithTwitter(): Promise<void> {
    try {
      await this.runInAuthContext(async (auth) => {
        if (!auth.currentUser) {
          return;
        }

        const provider = new TwitterAuthProvider();
        await linkWithPopup(auth.currentUser, provider);
      });
    } catch (error) {
      this.handleAuthError(error, 'Twitter account linking');
    }
  }

  public async unlinkProvider(providerId: string): Promise<void> {
    try {
      await this.runInAuthContext(async (auth) => {
        if (!auth.currentUser) {
          return;
        }

        await unlink(auth.currentUser, providerId);
      });
    } catch (error) {
      this.handleAuthError(error, 'Provider unlinking');
    }
  }

  public getProviderData(): { providerId: string; email?: string | null }[] {
    return (
      AuthService.currentFirebaseUser?.providerData.map((provider) => ({
        email: provider.email,
        providerId: provider.providerId,
      })) || []
    );
  }

  public refreshUserGroups(): void {
    if (AuthService.currentDbUser) {
      this.setCurrentUserGroup(AuthService.currentDbUser);
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.tokenRefreshedSubject.complete();
  }

  private async signInWithProvider(
    provider: GoogleAuthProvider | FacebookAuthProvider | TwitterAuthProvider,
    context: string,
  ): Promise<void> {
    try {
      this.prepareForSignIn();
      await this.runInAuthContext(async (auth) => {
        await signInWithPopup(auth, provider);
      });
    } catch (error) {
      this.handleSignInError(error, context);
    }
  }

  private prepareForSignIn(): void {
    this.isSigningIn = true;
    this.returnUrl = this.router.parseUrl(this.router.url).queryParams['returnUrl'] || '/';
  }

  private handleSignInError(error: unknown, context: string): void {
    this.isSigningIn = false;
    this.returnUrl = null;
    this.handleAuthError(error, context);
  }

  private handleTokenRequest(authUser: User | null, forceRefresh: boolean): Observable<string | null> {
    if (!authUser) {
      return of(null);
    }

    if (!forceRefresh) {
      const cachedToken = this.getCachedToken(authUser.uid);

      if (cachedToken) {
        return of(cachedToken);
      }
    }

    return from(authUser.getIdToken(forceRefresh)).pipe(
      map((token) => {
        this.cacheToken(authUser.uid, token);

        return token;
      }),
      catchError((error) => {
        this.handleAuthError(error, 'Get ID token');
      }),
    );
  }

  private handleRefreshedToken(token: string | null, currentUser: User | null): string | null {
    this.tokenRefreshInProgress = false;

    if (token && currentUser) {
      this.cacheToken(currentUser.uid, token);
      this.tokenRefreshedSubject.next();
    }

    return token;
  }

  private handleTokenError(error: unknown): Observable<never> {
    this.tokenRefreshInProgress = false;

    return this.handleAuthError(error, 'Token refresh');
  }

  private initAuthState(): void {
    const authStateObservable = this.runInAuthContext((auth) => authState(auth)).pipe(
      retry(AUTH_STATE_RETRIES),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.subscriptions.add(
      authStateObservable.subscribe({
        next: (currentUser) => {
          void this.handleAuthStateChange(currentUser as IFirebaseUser | null);
        },

        error: (error) => {
          void this.handleAuthStateError(error);
        },
      }),
    );
  }

  private async handleAuthStateChange(currentUser: IFirebaseUser | null): Promise<void> {
    AuthService.currentFirebaseUserSubject.next(currentUser);

    if (currentUser) {
      await this.handleUserAuthenticated();
    } else if (!this.isSigningIn) {
      this.isAuthenticating.next(false);
    }

    this.setInitialized();
  }

  private async handleUserAuthenticated(): Promise<void> {
    this.isAuthenticating.next(true);

    try {
      await this.fetchCurrentUser();
      await this.handleNavigation();
    } catch {
      // Another session-end path that doesn't go through signOut() - Firebase still thinks
      // there's a valid session (that's how we got into handleUserAuthenticated at all), but the
      // app-level user fetch/navigation failed, so this bounces to login the same as an
      // explicit sign-out would. See AppUpdateService.clearImageDataGroupCache.
      void this.appUpdateService.clearImageDataGroupCache();
      await this.router.navigate(['/auth/login']);
    } finally {
      this.cleanupAfterAuth();
    }
  }

  private async handleNavigation(): Promise<void> {
    if (this.isSigningIn && this.returnUrl) {
      await this.router.navigate([this.returnUrl]);
    } else if (this.router.url.includes('/auth/')) {
      const returnUrl = this.router.parseUrl(this.router.url).queryParams['returnUrl'] || '/';
      await this.router.navigate([returnUrl]);
    }
  }

  private cleanupAfterAuth(): void {
    if (this.isSigningIn) {
      this.isSigningIn = false;
      this.returnUrl = null;
    }

    this.isAuthenticating.next(false);
  }

  private handleAuthStateError(error: Error): void {
    this.setInitialized();
    this.isAuthenticating.next(false);
    this.handleAuthError(error, 'Auth state subscription');
  }

  private async fetchCurrentUser(): Promise<void> {
    try {
      const [dbUser, health] = await Promise.all([
        firstValueFrom(this.userService.getCurrentUser()),
        firstValueFrom(this.maintenanceService.getHealthStatus()),
      ]);

      dbUser.userGroups = [];
      dbUser.campaignVersion = health?.campaignVersion;

      this.setCurrentUserGroup(dbUser);

      AuthService.currentDbUserSubject.next(dbUser);
      this.setSentryUser(dbUser);

      if (dbUser?.language) {
        this.translate.use(dbUser.language);
      }
    } catch (error) {
      AuthService.currentDbUserSubject.next(null);
      Sentry.setUser(null);

      throw error;
    }
  }

  private setCurrentUserGroup(dbUser: IUser): void {
    if (!AuthService.currentFirebaseUser) {
      return;
    }

    this.userGroupService
      .getCurrentUserGroups()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }), take(1))
      .subscribe((userGroups) => {
        dbUser.userGroups = userGroups ?? [];
        this.setSentryUser(dbUser);
        SignalTriggerUtilitiesService.trigger(this.userGroupsChanged);
      });
  }

  private isTokenValid(uid: string): boolean {
    const cached = this.tokenCache.get(uid);

    return cached ? Date.now() < cached.expiry : false;
  }

  private getCachedToken(uid: string): string | null {
    return this.isTokenValid(uid) ? (this.tokenCache.get(uid)?.token ?? null) : null;
  }

  private cacheToken(uid: string, token: string): void {
    this.tokenCache.set(uid, {
      expiry: Date.now() + TOKEN_EXPIRY_MS,
      token,
    });
  }

  private clearTokenCache(): void {
    this.tokenCache.clear();
  }

  private runInAuthContext<T>(operation: (auth: Auth) => T): T {
    return runInInjectionContext(this.injector, () => operation(this.auth));
  }

  private handleAuthError(error: unknown, context: string): never {
    // eslint-disable-next-line no-console
    console.error(`${context} error:`, error);

    throw error;
  }

  private setSentryUser(dbUser: IUser): void {
    const firebaseUser = AuthService.currentFirebaseUser;
    const currentGroup = dbUser.userGroups?.find((g) => g.id === dbUser.userGroupId);

    Sentry.setUser({
      email: firebaseUser?.email ?? undefined,
      id: dbUser.id,
      userGroupId: dbUser.userGroupId,
      userGroupName: currentGroup?.name,
      username: dbUser.name ?? undefined,
    });
  }

  private setInitialized(): void {
    if (!this.isInitialized.value) {
      this.isInitialized.next(true);
    }
  }
}
